import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import meetingsAPI from '../services/meetingsApi';
import liveAPI, { LiveStreamHandle } from '../services/liveApi';
import { Meeting } from '../types/meeting';
import {
  LiveState,
  LiveInsight,
  SuggestedQuestion,
  TranscriptSegment,
  QuestionAction,
  TeamsFinalCaption,
  buildSlideRef,
  splitPasteIntoSegments,
} from '../types/live';
import { DeckFile, PageCounts, isPdfFile, resolveDeckFiles } from '../types/deck';
import SlideStage from '../components/views/live/SlideStage';
import PresenterDock from '../components/views/live/PresenterDock';
import AssistantPanel from '../components/views/live/AssistantPanel';
import RaisedQuestionsControl, { QueuedRaise } from '../components/views/live/RaisedQuestionsControl';
import ViewingAsSwitcher from '../components/views/live/ViewingAsSwitcher';
import { useMicTranscription } from '../components/views/live/useMicTranscription';
import { useTeamsCaptions } from '../components/views/live/useTeamsCaptions';
import { LiveEventBatch, useLiveEventBuffer } from '../components/views/live/useLiveEventBuffer';
import type { DockBusyAction } from '../components/views/live/PresenterDock';
import SpinningLoader from '../components/ui/SpinningLoader';
import Toast from '../components/ui/Toast';
import '../styles/live.css';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const EMPTY_LIVE_STATE: LiveState = {
  is_live: false,
  active_slide_ref: null,
  transcriber_status: 'stopped',
  controller_user_id: null,
};

// Upsert a transcript segment keeping the list ordered by sequence and
// deduplicated (the stream replays stored segments on connect).
const upsertSegment = (list: TranscriptSegment[], seg: TranscriptSegment): TranscriptSegment[] => {
  const idx = list.findIndex((s) => s.sequence === seg.sequence);
  const next = idx === -1 ? [...list, seg] : list.map((s) => (s.sequence === seg.sequence ? seg : s));
  return next.sort((a, b) => a.sequence - b.sequence);
};

const upsertById = <T extends { id: string }>(list: T[], item: T): T[] => {
  const idx = list.findIndex((x) => x.id === item.id);
  return idx === -1 ? [...list, item] : list.map((x) => (x.id === item.id ? item : x));
};

// Live insights and questions accrue for the whole session. They are grouped by
// slide so the linkage is clear and the history of each visited slide is kept
// (returning to a slide shows its earlier items). We cap PER GROUP — the pack
// (meeting-level top insights) and each slide — rather than with a single global
// window, so items from one slide never evict another's history. The Context
// stream shows a "Show N more" disclosure instead of dropping items, so the
// cap only guards against unbounded growth.
const MAX_ITEMS_PER_GROUP = 12;

// How long an independently-browsing follower must settle on a slide before we
// regenerate their insights for it (Unit 15c). Each generation is an LLM call,
// so this is deliberately longer than a page-flick: stepping through a deck
// costs one call for the slide the reader stops on, not one per slide passed.
const BROWSE_REFRESH_DEBOUNCE_MS = 1500;

// When a refresh finishes but the follower has since moved on, the newer slide
// is chased after this pause. Short (the user is already waiting) but non-zero
// so the server's in-flight slot is released before the next attempt.
const BROWSE_REFRESH_RETRY_MS = 400;

// Cap on those chases per settle, so a persistently failing refresh cannot spin.
const BROWSE_REFRESH_MAX_RETRIES = 3;

// Group key: pack insights (slide-agnostic) live in their own bucket; everything
// else is keyed by its slide_ref. A missing slide_ref falls back to the pack
// bucket for pack-category items, else an "unassigned" bucket.
const insightGroupKey = (ins: LiveInsight): string =>
  ins.category === 'pack' ? '__pack__' : ins.slide_ref || '__unassigned__';

const questionGroupKey = (q: SuggestedQuestion): string => q.slide_ref || '__unassigned__';

// Upsert an item and cap only its own group, preserving all other groups' order.
const upsertGroupedCapped = <T extends { id: string }>(
  list: T[],
  item: T,
  keyOf: (item: T) => string
): T[] => {
  const next = upsertById(list, item);
  const key = keyOf(item);
  const inGroup = next.filter((i) => keyOf(i) === key);
  if (inGroup.length <= MAX_ITEMS_PER_GROUP) return next;
  // Drop the oldest members of this group only (they retain insertion order).
  const excess = new Set(inGroup.slice(0, inGroup.length - MAX_ITEMS_PER_GROUP).map((i) => i.id));
  return next.filter((i) => !excess.has(i.id));
};

// Cap each group of an initial list independently (used for the initial load).
const capGroups = <T extends { id: string }>(list: T[], keyOf: (item: T) => string): T[] => {
  const counts: Record<string, number> = {};
  list.forEach((i) => { counts[keyOf(i)] = (counts[keyOf(i)] || 0) + 1; });
  const remainingToDrop: Record<string, number> = {};
  let anyToDrop = false;
  Object.keys(counts).forEach((k) => {
    if (counts[k] > MAX_ITEMS_PER_GROUP) {
      remainingToDrop[k] = counts[k] - MAX_ITEMS_PER_GROUP;
      anyToDrop = true;
    }
  });
  if (!anyToDrop) return list;
  return list.filter((i) => {
    const k = keyOf(i);
    if (remainingToDrop[k] > 0) {
      remainingToDrop[k] -= 1;
      return false;
    }
    return true;
  });
};

const LiveMeeting: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isSecretary } = useAuth();

  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [materials, setMaterials] = useState<DeckFile[]>([]);
  const [loading, setLoading] = useState(true);
  // Materials load independently of the meeting so the slide stage can tell
  // "still loading" apart from "no PDFs in this deck" (M2).
  const [materialsLoading, setMaterialsLoading] = useState(true);
  const [materialsError, setMaterialsError] = useState(false);
  const [liveState, setLiveState] = useState<LiveState>(EMPTY_LIVE_STATE);
  const [segments, setSegments] = useState<TranscriptSegment[]>([]);
  const [insights, setInsights] = useState<LiveInsight[]>([]);
  const [questions, setQuestions] = useState<SuggestedQuestion[]>([]);
  // True while the initial insights/questions fetch is in flight, so the
  // Context tab can show skeletons instead of a premature empty message.
  const [contextLoading, setContextLoading] = useState(true);
  // Presenter-only FIFO queue of questions participants have raised to the
  // presenter (fed by the whole-meeting question.raised / .addressed / .lowered
  // events). Owned here so it survives panel open/close and SSE reconnects.
  const [raisedQueue, setRaisedQueue] = useState<QueuedRaise[]>([]);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatStreaming, setChatStreaming] = useState(false);
  const [reconnecting, setReconnecting] = useState(false);
  // Per-action busy so a control shows its own operation label instead of a
  // global spinner disabling unrelated controls silently (M5). null = idle.
  const [busyAction, setBusyAction] = useState<DockBusyAction | null>(null);
  // Mobile (≤720px) bottom-sheet snap for the assistant: peek keeps the stage
  // in full view, half shows the stage and the Context stream together, full
  // is an (almost) fullscreen assistant. Secretaries start at peek (they
  // present); followers start at half (their tools alongside the slide).
  const [sheetSnap, setSheetSnap] = useState<'peek' | 'half' | 'full'>('peek');
  // Per-file page counts discovered by the stage's PDF loaders, lifted here so
  // the assistant panel can label slides with the same global numbering as the
  // stage's "Slide X of N" counter.
  const [pageCounts, setPageCounts] = useState<PageCounts>({});

  const [toastVisible, setToastVisible] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  const streamRef = useRef<LiveStreamHandle | null>(null);
  const maxSequenceRef = useRef<number>(-1);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error' | 'info' | 'warning' = 'info') => {
      setToastMessage(message);
      setToastType(type);
      setToastVisible(true);
    },
    []
  );

  const personaRole = user?.role || 'member';

  // Track the highest sequence seen so mic segments start after it.
  useEffect(() => {
    for (const s of segments) {
      if (s.sequence > maxSequenceRef.current) maxSequenceRef.current = s.sequence;
    }
  }, [segments]);

  // Load meeting + initial insights/questions.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const m = await meetingsAPI.getMeeting(id);
        if (cancelled) return;
        setMeeting(m);
      } catch (err) {
        if (!cancelled) {
          showToast('Failed to load meeting', 'error');
          setTimeout(() => navigate('/meetings'), 1500);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
      try {
        const [ins, qs] = await Promise.all([
          liveAPI.getInsights(id, 'live'),
          liveAPI.getQuestions(id),
        ]);
        if (cancelled) return;
        setInsights(capGroups(ins, insightGroupKey));
        setQuestions(capGroups(qs, questionGroupKey));
      } finally {
        if (!cancelled) setContextLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate, showToast]);

  // Materials load on their own so the slide stage can distinguish loading from
  // an empty deck, and offer Retry on failure (M2). Independent of the meeting
  // fetch above, whose page loader no longer blocks on this.
  const loadMaterials = useCallback(async () => {
    if (!id) return;
    setMaterialsLoading(true);
    setMaterialsError(false);
    try {
      const files = await meetingsAPI.listFiles(id);
      setMaterials(files.map((f) => ({ fileId: f.file_id, filename: f.filename })));
    } catch {
      setMaterialsError(true);
    } finally {
      setMaterialsLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  // Insight/question SSE arrivals land in bursts (up to 10 events per
  // generation trigger). Buffer them into one state commit per window so the
  // panel re-renders once per batch instead of once per event.
  const commitLiveBatch = useCallback((batch: LiveEventBatch) => {
    if (batch.insights.length > 0) {
      setInsights((prev) =>
        batch.insights.reduce((acc, ins) => upsertGroupedCapped(acc, ins, insightGroupKey), prev)
      );
    }
    if (batch.questions.length > 0) {
      setQuestions((prev) =>
        batch.questions.reduce((acc, q) => upsertGroupedCapped(acc, q, questionGroupKey), prev)
      );
    }
  }, []);
  const { pushInsight, pushQuestion } = useLiveEventBuffer(commitLiveBatch);

  // Open the SSE stream once the meeting is known. One EventSource per view.
  useEffect(() => {
    if (!id || !meeting) return;
    const handle = liveAPI.openStream(id, {
      onOpen: () => setReconnecting(false),
      onReconnecting: () => setReconnecting(true),
      onLiveState: (state) => {
        setReconnecting(false);
        setLiveState(state);
      },
      onSlideChanged: (slideRef, state) => {
        setLiveState((prev) => (state ? state : { ...prev, active_slide_ref: slideRef }));
      },
      onSegment: (seg) => setSegments((prev) => upsertSegment(prev, seg)),
      onCleared: () => {
        setSegments([]);
        maxSequenceRef.current = -1;
      },
      onInsight: pushInsight,
      onQuestion: pushQuestion,
      onQuestionRaised: (raised) =>
        setRaisedQueue((prev) =>
          prev.some((r) => r.question_id === raised.question_id)
            ? prev
            : [...prev, { ...raised, received_at: Date.now() }].sort(
                (a, b) => Date.parse(a.raised_at) - Date.parse(b.raised_at) || a.received_at - b.received_at
              )
        ),
      onQuestionRemoved: (removal) =>
        setRaisedQueue((prev) => prev.filter((r) => r.question_id !== removal.question_id)),
      onError: (message) => showToast(message, 'error'),
    });
    streamRef.current = handle;
    return () => {
      handle.close();
      streamRef.current = null;
    };
  }, [id, meeting, showToast, pushInsight, pushQuestion]);

  const canControl = isSecretary;

  // Followers land with the sheet at half (stage + their tools together);
  // secretaries at peek. Applied once the role is known (mobile-only).
  useEffect(() => {
    setSheetSnap(canControl ? 'peek' : 'half');
  }, [canControl]);

  // ---- Controller actions ----
  // Each action tags itself so the affected control (and only it) shows a
  // progress label; a slide change does not disable Stop Live and vice versa.
  const runControl = useCallback(
    async (action: DockBusyAction, fn: () => Promise<void>, failMessage: string) => {
      setBusyAction(action);
      try {
        await fn();
      } catch (err) {
        showToast(err instanceof Error ? err.message : failMessage, 'error');
      } finally {
        setBusyAction((prev) => (prev === action ? null : prev));
      }
    },
    [showToast]
  );

  const handleStartLive = () =>
    runControl('start-live', async () => {
      const state = await liveAPI.startLive(id!);
      setLiveState(state);
      showToast('Live session started', 'success');
    }, 'Failed to start live');

  const handleStopLive = () =>
    runControl('stop-live', async () => {
      const state = await liveAPI.stopLive(id!);
      setLiveState(state);
      showToast('Live session stopped', 'info');
    }, 'Failed to stop live');

  const handleTranscriber = (action: 'start' | 'stop') =>
    runControl('transcriber', async () => {
      const state = await liveAPI.controlTranscriber(id!, action);
      setLiveState(state);
    }, 'Failed to control transcriber');

  // Stable across renders so React.memo(SlideStage) actually holds: LiveMeeting
  // re-renders on every SSE event (segments, interim mic results, insights,
  // questions), and an unstable callback here would re-render the react-pdf Page
  // each time — the visible slide "flicker" during a live meeting.
  const handleRequestPage = useCallback(
    (fileId: string, page: number) =>
      runControl('slide', async () => {
        const state = await liveAPI.setSlide(id!, buildSlideRef(fileId, page));
        setLiveState(state);
      }, 'Failed to change slide'),
    [id, runControl]
  );

  // ---- Mic capture ----
  // Azure diarization (ConversationTranscriber) separates voices and hands each
  // finalized utterance a friendly "Speaker N" label, which we attach to the
  // segment. The Web Speech fallback has no diarization and posts anonymously.
  const handleFinalMicResult = useCallback(
    (text: string, speaker?: string) => {
      if (!id) return;
      const nextSequence = maxSequenceRef.current + 1;
      maxSequenceRef.current = nextSequence;
      liveAPI
        .postSegments(
          id,
          [{ sequence: nextSequence, speaker: speaker || undefined, text, timestamp: new Date().toISOString() }],
          'mic'
        )
        .catch((err) => showToast(err instanceof Error ? err.message : 'Failed to send mic segment', 'error'));
    },
    [id, showToast]
  );

  // Secretary paste affordance (live Transcript tab): split pasted text into
  // segments and post them (source "paste") so live insights react mid-session.
  // Sequence numbers continue after the highest segment already seen.
  const handlePasteTranscript = useCallback(
    async (text: string): Promise<boolean> => {
      if (!id) return false;
      const segs = splitPasteIntoSegments(text, maxSequenceRef.current, new Date().toISOString());
      if (segs.length === 0) return false;
      maxSequenceRef.current = segs[segs.length - 1].sequence;
      try {
        await liveAPI.postSegments(id, segs, 'paste');
        return true;
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to add transcript', 'error');
        return false;
      }
    },
    [id, showToast]
  );

  // Secretary clear affordance (live Transcript tab): wipes stored segments and
  // the whole-blob transcript server-side; the transcript.cleared broadcast
  // resets every open view (including this one) via onCleared above.
  const handleClearTranscript = useCallback(async (): Promise<boolean> => {
    if (!id) return false;
    try {
      await liveAPI.clearSegments(id);
      return true;
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to clear transcript', 'error');
      return false;
    }
  }, [id, showToast]);

  // Azure AI Speech when configured (low-latency, high accuracy), else the
  // browser Web Speech API. Same interface either way.
  const mic = useMicTranscription(id, handleFinalMicResult);

  // ---- Teams captions feed (teams-live-transcription-plan.md Unit T3) ----
  // Final Teams captions become "feed" segments, sequenced exactly like mic
  // results. Mic and Teams are mutually exclusive so the session has a single
  // sequence writer.
  const handleFinalTeamsCaption = useCallback(
    (caption: TeamsFinalCaption) => {
      if (!id) return;
      const nextSequence = maxSequenceRef.current + 1;
      maxSequenceRef.current = nextSequence;
      liveAPI
        .postSegments(
          id,
          [{ sequence: nextSequence, speaker: caption.speaker, text: caption.text, timestamp: caption.timestamp }],
          'feed'
        )
        .catch((err) => showToast(err instanceof Error ? err.message : 'Failed to send Teams segment', 'error'));
    },
    [id, showToast]
  );

  const teams = useTeamsCaptions(id, handleFinalTeamsCaption);

  const handleJoinTeams = (joinUrl: string) => {
    if (mic.listening) mic.stop();
    teams.join(joinUrl);
  };

  const handleToggleMic = () => {
    if (mic.listening) {
      mic.stop();
    } else {
      if (teams.active) teams.leave();
      mic.start();
    }
  };

  useEffect(() => {
    if (mic.error) showToast(`Microphone: ${mic.error}`, 'warning');
  }, [mic.error, showToast]);

  useEffect(() => {
    if (teams.error) showToast(`Teams: ${teams.error}`, 'warning');
  }, [teams.error, showToast]);

  // Transcription is bound entirely to the caption source: whenever the mic is
  // listening or the Teams agent is in the call, the backend transcriber must be
  // running. There is no manual transcriber control, so we (re)assert start
  // whenever a source is active and the transcriber is not already running —
  // which also recovers from a failed or missed start attempt. `start` is
  // idempotent on the backend. The ref only dedupes a request already in flight
  // so we don't spam identical POSTs across re-renders.
  const startInFlightRef = useRef(false);
  const sourceActive = mic.listening || teams.active;
  useEffect(() => {
    if (!sourceActive || liveState.transcriber_status === 'running') {
      startInFlightRef.current = false;
      return;
    }
    if (startInFlightRef.current) return;
    startInFlightRef.current = true;
    Promise.resolve(handleTranscriber('start')).finally(() => {
      startInFlightRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceActive, liveState.transcriber_status]);

  // Leaving the live session takes the Teams agent out of the call too.
  useEffect(() => {
    if (!liveState.is_live && teams.active) teams.leave();
  }, [liveState.is_live, teams]);

  // A new session starts with an empty raised-hands queue; clear it whenever the
  // session is not live so stale raises from a prior session don't linger.
  useEffect(() => {
    if (!liveState.is_live) setRaisedQueue([]);
  }, [liveState.is_live]);

  // ---- Ask AI ----
  const handleSendChat = useCallback(
    async (query: string) => {
      if (!id) return;
      setChatMessages((prev) => [...prev, { role: 'user', content: query }]);
      setChatStreaming(true);
      let assistantIndex = -1;
      setChatMessages((prev) => {
        assistantIndex = prev.length;
        return [...prev, { role: 'assistant', content: '' }];
      });
      try {
        await liveAPI.streamChat(id, query, (delta) => {
          setChatMessages((prev) => {
            const next = [...prev];
            const target = next[assistantIndex];
            if (target && target.role === 'assistant') {
              next[assistantIndex] = { role: 'assistant', content: target.content + delta };
            }
            return next;
          });
        });
      } catch (err) {
        setChatMessages((prev) => {
          const next = [...prev];
          if (next[assistantIndex]?.role === 'assistant') {
            next[assistantIndex] = {
              role: 'assistant',
              content: `Sorry, something went wrong: ${err instanceof Error ? err.message : 'unknown error'}`,
            };
          }
          return next;
        });
      } finally {
        setChatStreaming(false);
      }
    },
    [id]
  );

  // ---- Manual insight refresh (Context tab) ----
  // Regenerates the caller's live batch on demand. The response carries the
  // full post-regeneration set (also arriving via SSE; upsert is idempotent).
  const [refreshingInsights, setRefreshingInsights] = useState(false);
  const handleRefreshInsights = useCallback(async () => {
    if (!id || refreshingInsights) return;
    setRefreshingInsights(true);
    try {
      const { insights: ins, questions: qs } = await liveAPI.regenerateInsights(id);
      setInsights(capGroups(ins, insightGroupKey));
      setQuestions(capGroups(qs, questionGroupKey));
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Failed to refresh insights', 'error');
    } finally {
      setRefreshingInsights(false);
    }
  }, [id, refreshingInsights, showToast]);

  // ---- Follower browse refresh (Unit 15c) ----
  // A non-controller browsing the deck on their own never touches the shared
  // slide_ref, so the server's slide-change trigger (PUT live/slide) never fires
  // for them and their Context stream stays on the presenter's page. Regenerate
  // the caller's own batch for the slide they land on instead.
  //
  // Debounced: paging through several slides must not stack one LLM call per
  // step, so the request only goes out once the user has settled. A null ref
  // (they snapped back to following the presenter) just cancels the pending
  // timer — following again is not a reason to generate. The last requested ref
  // is remembered so re-reporting the same slide (a re-render, or stepping away
  // and back within the window) does not re-request it.
  const browseRefreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // The slide we have actually refreshed for (set only on success), so a failed
  // or superseded attempt is retried rather than treated as done.
  const lastBrowseRefreshRef = useRef<string | null>(null);
  // The slide the follower is on *now*. Read inside async callbacks to decide
  // whether an arriving batch is still wanted and whether to chase a newer one.
  const desiredBrowseRef = useRef<string | null>(null);
  const browseRequestInFlight = useRef(false);
  const browseRetryCount = useRef(0);
  // Drives the panel's existing Refreshing… affordance while a browse refresh is
  // pending or running. Without it the follower sees the new slide's empty group
  // and its "New items appear as the presentation continues" copy, which
  // describes waiting on the presenter — the opposite of what is happening,
  // since we are generating for them right now. Set from the moment they settle,
  // not from the request, so the debounce window is not silent either.
  const [browseRefreshing, setBrowseRefreshing] = useState(false);
  // Where the follower is looking, so the Context stream foregrounds that
  // slide's group instead of the presenter's while they browse.
  const [browsedSlideRef, setBrowsedSlideRef] = useState<string | null>(null);

  // Issue the refresh for `slideRef`, then reconcile against where the user has
  // moved to in the meantime. Explicit last-write-wins: only one generation runs
  // at a time, and a request arriving while one is in flight is not dropped —
  // `desiredBrowseRef` is the single-slot pending ref, so repeated arrivals
  // coalesce to the newest one (a queue deeper than one is never needed). When
  // the in-flight call completes, the pending ref is run if it still differs
  // from what was last generated.
  //
  // The case this exists for: browse p5 (generation starts, ~8s), browse p9 at
  // t+2s. Without this, p9's request meets the server's `_manualInFlight` dedupe,
  // takes a 409, and — since the user has stopped browsing, so no further trigger
  // fires — p9 never gets insights at all. Here p9 is chased when p5 finishes.
  const runBrowseRefresh = useCallback(
    (slideRef: string) => {
      if (!id || browseRequestInFlight.current) return;
      browseRequestInFlight.current = true;
      setBrowseRefreshing(true);
      liveAPI
        .regenerateInsights(id, slideRef)
        .then(({ insights: ins, questions: qs }) => {
          lastBrowseRefreshRef.current = slideRef;
          browseRetryCount.current = 0;
          // Stale-response guard: a batch for a slide the user has already left
          // must not replace what they are looking at now.
          if (desiredBrowseRef.current !== slideRef) return;
          setInsights(capGroups(ins, insightGroupKey));
          setQuestions(capGroups(qs, questionGroupKey));
        })
        .catch((err: Error & { status?: number }) => {
          // A superseded request's failure is not the reader's problem: they are
          // on another slide, and its own refresh governs what they see. Ahead of
          // the 409 branch so a stale rejection neither toasts nor spends the
          // retry budget, while the reconcile below still chases the newer slide.
          if (desiredBrowseRef.current !== slideRef) return;
          // 409 = a generation for this user is already running (the server's
          // dedupe). Leave lastBrowseRefreshRef unset so the reconcile below
          // retries instead of stranding the slide with no insights.
          if (err?.status === 409) return;
          browseRetryCount.current = BROWSE_REFRESH_MAX_RETRIES;
          showToast(err instanceof Error ? err.message : 'Failed to refresh insights', 'error');
        })
        .finally(() => {
          browseRequestInFlight.current = false;
          // One predicate decides both whether to chase and whether the slide is
          // still being worked on, so the spinner cannot disagree with reality.
          // Clearing on `desired === slideRef` alone would strand it on forever
          // in the two cases where no chase follows a mismatch: the follower
          // snapped back to the presenter (desired is null), or the retry budget
          // is spent. Clearing unconditionally would instead flicker it off
          // mid-coalesce while the chased generation is still running.
          const desired = desiredBrowseRef.current;
          const willChase =
            !!desired &&
            desired !== lastBrowseRefreshRef.current &&
            browseRetryCount.current < BROWSE_REFRESH_MAX_RETRIES;
          if (!willChase) {
            setBrowseRefreshing(false);
            return;
          }
          browseRetryCount.current += 1;
          if (browseRefreshTimer.current) clearTimeout(browseRefreshTimer.current);
          browseRefreshTimer.current = setTimeout(() => {
            browseRefreshTimer.current = null;
            // Re-read rather than closing over the ref seen at completion: the
            // follower may have moved again during the pause, and only the
            // newest pending slide is worth an LLM call.
            const chase = desiredBrowseRef.current;
            if (chase && chase !== lastBrowseRefreshRef.current) runBrowseRefresh(chase);
          }, BROWSE_REFRESH_RETRY_MS);
        });
    },
    [id, showToast]
  );

  const handleBrowseSlideChange = useCallback(
    (slideRef: string | null) => {
      setBrowsedSlideRef(slideRef);
      desiredBrowseRef.current = slideRef;
      if (browseRefreshTimer.current) {
        clearTimeout(browseRefreshTimer.current);
        browseRefreshTimer.current = null;
      }
      if (!id || !slideRef) {
        lastBrowseRefreshRef.current = null;
        browseRetryCount.current = 0;
        // Snapping back to the presenter cancels the pending refresh, so nothing
        // is coming and the spinner must not outlive it. Guarded by the
        // in-flight check: a running generation still owns the indicator.
        if (!browseRequestInFlight.current) setBrowseRefreshing(false);
        return;
      }
      if (slideRef === lastBrowseRefreshRef.current) return;

      browseRetryCount.current = 0;
      setBrowseRefreshing(true);
      browseRefreshTimer.current = setTimeout(() => {
        browseRefreshTimer.current = null;
        runBrowseRefresh(slideRef);
      }, BROWSE_REFRESH_DEBOUNCE_MS);
    },
    [id, runBrowseRefresh]
  );

  useEffect(
    () => () => {
      if (browseRefreshTimer.current) clearTimeout(browseRefreshTimer.current);
    },
    []
  );

  // ---- Question actions ----
  // Failures propagate to the panel, which restores the activated control and
  // shows the error inline on the card (no toast).
  const handleActQuestion = useCallback(
    async (question: SuggestedQuestion, action: QuestionAction) => {
      if (!id) return;
      const updated = await liveAPI.actOnQuestion(id, question.id, action);
      setQuestions((prev) => upsertById(prev, updated));
    },
    [id]
  );

  // Presenter marks a raised question as addressed. The queue entry is removed
  // optimistically; the server's question.addressed broadcast also removes it
  // (idempotent) for other presenter connections.
  const handleAddressQuestion = useCallback(
    async (questionId: string) => {
      if (!id) return;
      try {
        await liveAPI.actOnQuestion(id, questionId, 'address');
        setRaisedQueue((prev) => prev.filter((r) => r.question_id !== questionId));
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Failed to mark the question addressed', 'error');
      }
    },
    [id, showToast]
  );

  const sectionLabel = useMemo(() => meeting?.title || 'Live meeting', [meeting]);

  // Count of context items (insights + suggested questions) for the slide on
  // stage — drives the ✦ chip on the stage topbar linking stage to assistant.
  const activeSlideContextCount = useMemo(() => {
    const ref = liveState.active_slide_ref;
    if (!ref) return 0;
    return (
      insights.filter((i) => i.category !== 'pack' && i.slide_ref === ref).length +
      questions.filter((q) => q.slide_ref === ref).length
    );
  }, [insights, questions, liveState.active_slide_ref]);

  // Build the combined presentation deck: the secretary's picked order
  // (presentation_file_ids) when present, else all PDF materials in listing
  // order (legacy behaviour). slide.changed may reference an off-deck file; the
  // stage tolerates that by loading it ad hoc.
  const pdfMaterials = useMemo(() => materials.filter((m) => isPdfFile(m.filename)), [materials]);
  const deckFiles = useMemo(
    () => resolveDeckFiles(liveState.presentation_file_ids, pdfMaterials),
    [liveState.presentation_file_ids, pdfMaterials]
  );

  // Auto-present the first slide of the picked deck when a controller lands on a
  // live session that has no active slide yet. live:start leaves
  // active_slide_ref null (the client owns the "{fileId}:{page}" format), so
  // without this seed the secretary is stuck on a blank canvas with Prev/Next
  // disabled — this also heals sessions that were already left live and stuck.
  // Guarded by a ref so SSE re-emissions or a failed request don't loop it; the
  // guard re-arms when the session goes not-live.
  const autoSeededRef = useRef(false);
  useEffect(() => {
    if (!liveState.is_live) {
      autoSeededRef.current = false;
      return;
    }
    if (
      canControl &&
      !liveState.active_slide_ref &&
      deckFiles.length > 0 &&
      !autoSeededRef.current
    ) {
      autoSeededRef.current = true;
      handleRequestPage(deckFiles[0].fileId, 1);
    }
  }, [canControl, liveState.is_live, liveState.active_slide_ref, deckFiles, handleRequestPage]);

  if (loading || !meeting) {
    return (
      <div className="live-page">
        <div className="dashboard-loading">
          <SpinningLoader size="large" text="Loading live meeting…" />
        </div>
      </div>
    );
  }

  return (
    <div className="live-page">
      <div className="live-header">
        <button className="meeting-back-btn" onClick={() => navigate(`/meetings/${id}`)}>
          ← Back to workspace
        </button>
        <h1 className="live-title">{meeting.title}</h1>
        {canControl && liveState.is_live && (
          <RaisedQuestionsControl queue={raisedQueue} onAddress={handleAddressQuestion} />
        )}
        <ViewingAsSwitcher role={personaRole} displayName={user?.display_name} title={user?.title} />
      </div>

      {reconnecting && (
        <div className="live-reconnect-banner" role="status">
          Connection interrupted — showing the last received update. Reconnecting…
        </div>
      )}

      <div className={`live-body live-sheet-${sheetSnap}`}>
        <div className="live-main">
          <SlideStage
            slideRef={liveState.active_slide_ref}
            deckFiles={deckFiles}
            documents={pdfMaterials}
            isLive={liveState.is_live}
            reconnecting={reconnecting}
            startedAt={liveState.started_at}
            lastSessionSeconds={liveState.last_session_seconds}
            fallbackLabel={sectionLabel}
            canControl={canControl && liveState.is_live}
            isController={canControl}
            allowIndependentBrowse={!canControl && liveState.is_live}
            materialsLoading={materialsLoading}
            materialsError={materialsError}
            contextCount={activeSlideContextCount}
            onRetryMaterials={loadMaterials}
            onRequestPage={handleRequestPage}
            onBrowseSlideChange={handleBrowseSlideChange}
            onPageCountsChange={setPageCounts}
          />
          {canControl && (
            <PresenterDock
              liveState={liveState}
              busyAction={busyAction}
              canGoLive={meeting.status === 'prepared'}
              micSupported={mic.supported}
              micListening={mic.listening}
              teamsStatus={teams.status}
              teamsError={teams.error}
              teamsActive={teams.active}
              initialJoinUrl={meeting.teams_join_url}
              onStartLive={handleStartLive}
              onStopLive={handleStopLive}
              onToggleMic={handleToggleMic}
              onJoinTeams={handleJoinTeams}
              onLeaveTeams={teams.leave}
            />
          )}
        </div>

        <div className="live-side">
          {/* Mobile-only (≤720px) bottom-sheet grabber: the assistant is a
              sheet over the stage with three snap heights, so the slide and
              the Context stream can be visible together. Tap cycles the snaps. */}
          <button
            type="button"
            className="live-sheet-grabber"
            aria-label={`Assistant panel, ${sheetSnap} height. Activate to ${
              sheetSnap === 'peek' ? 'expand to half' : sheetSnap === 'half' ? 'expand fully' : 'collapse'
            }.`}
            onClick={() =>
              setSheetSnap((s) => (s === 'peek' ? 'half' : s === 'half' ? 'full' : 'peek'))
            }
          >
            <span className="live-sheet-grabber-bar" aria-hidden="true" />
          </button>
          <AssistantPanel
            segments={segments}
            interimTranscript={mic.interim}
            insights={insights}
            questions={questions}
            activeSlideRef={browsedSlideRef ?? liveState.active_slide_ref}
            deckFiles={deckFiles}
            pageCounts={pageCounts}
            chatMessages={chatMessages}
            chatStreaming={chatStreaming}
            canPaste={canControl}
            canRefresh={liveState.is_live}
            refreshing={refreshingInsights || browseRefreshing}
            contextLoading={contextLoading}
            isLive={liveState.is_live}
            onRefreshInsights={handleRefreshInsights}
            onPasteTranscript={handlePasteTranscript}
            onClearTranscript={handleClearTranscript}
            onSendChat={handleSendChat}
            onActQuestion={handleActQuestion}
          />
        </div>
      </div>

      <Toast
        message={toastMessage}
        type={toastType}
        isVisible={toastVisible}
        onClose={() => setToastVisible(false)}
        duration={4000}
      />
    </div>
  );
};

export default LiveMeeting;
